import type {ReactElement} from 'react';
import type {ChatMessagePartType} from '@Components/ai-chat-window/ai-chat-window.types';
import type {PlanTableComponentPart} from '@Components/ai-chat-window/components/plan-table/plan-table.types';
import type {ButtonGroupComponentPart} from '@Components/ai-chat-window/components/button-group/button-group.types';
import type {EntityDraftCardComponentPart} from '@Components/ai-chat-window/components/entity-draft-card/entity-draft-card.types';
import type {EntityDraftSkeletonComponentPart} from '@Components/ai-chat-window/components/entity-draft-skeleton/entity-draft-skeleton.types';
import type {EntityCardComponentPart} from '@Components/ai-chat-window/components/entity-card/entity-card.types';
import type {EntityCardSkeletonComponentPart} from '@Components/ai-chat-window/components/entity-card-skeleton/entity-card-skeleton.types';
import type {EntityDraftGroupComponentPart} from '@Components/ai-chat-window/components/entity-draft-group/entity-draft-group.types';
import type {EntityCardGroupComponentPart} from '@Components/ai-chat-window/components/entity-card-group/entity-card-group.types';
import type {OptionGridComponentPart} from '@Components/ai-chat-window/components/option-grid/option-grid.types';
import type {ActionListComponentPart} from '@Components/ai-chat-window/components/action-list/action-list.types';
import type {DraftDiscardedNoteComponentPart} from '@Components/ai-chat-window/components/draft-discarded-note/draft-discarded-note.types';
import type {WebSourcesComponentPart} from '@Components/ai-chat-window/components/web-sources/web-sources.types';
import type {WebSourcesSkeletonComponentPart} from '@Components/ai-chat-window/components/web-sources-skeleton/web-sources-skeleton.types';

export enum StreamComponentType {
  PLAN_TABLE = 'plan_table',
  BUTTON_GROUP = 'button_group',
  BUTTON_GROUP_SKELETON = 'button_group_skeleton',
  SURVEY = 'survey',
  ENTITY_DRAFT = 'entity_draft',
  ENTITY_DRAFT_SKELETON = 'entity_draft_skeleton',
  ENTITY_CARD = 'entity_card',
  ENTITY_CARD_SKELETON = 'entity_card_skeleton',
  ENTITY_DRAFT_GROUP = 'entity_draft_group',
  ENTITY_CARD_GROUP = 'entity_card_group',
  OPTION_GRID = 'option_grid',
  ACTION_LIST = 'action_list',
  DRAFT_DISCARDED = 'draft_discarded',
  WEB_SOURCES = 'web_sources',
  WEB_SOURCES_SKELETON = 'web_sources_skeleton',
}

/** The prop a component carries when it answers a draft (the entity made from it, a revised draft): the draft's message id, or the ids of every draft it answers at once. */
export const ANSWERS_DRAFT_ID_PROP = 'answersDraftId';

/** The prop of the marker recording that the user dropped the drafts waiting for approval: their message ids. */
export const DISCARDED_DRAFT_IDS_PROP = 'discardedDraftIds';

export interface AnswersDraftProps {
  [ANSWERS_DRAFT_ID_PROP]?: string | string[];
  [DISCARDED_DRAFT_IDS_PROP]?: string[];
}

/** A component's props once its registration has parsed them, before they are typed as that component's part. */
export type ParsedStreamComponentProps = Record<string, unknown>;

export interface StreamComponentContext {
  chatId: string;
}

export interface StreamComponentRegistration<TComponentType extends StreamComponentType, TProps> {
  componentType: TComponentType;
  /** Validates the LLM-authored payload; returns null for a malformed shape so the part is dropped instead of crashing at render. */
  parseProps: (props: Record<string, unknown>) => TProps | null;
  render: (props: TProps, context: StreamComponentContext) => ReactElement;
}

/**
 * A props-type-erased registration for the registry. parse-then-render happen at
 * different times (parse on stream receipt, render later from the stored part), so
 * unlike the entity-card action they can't be fused; instead defineStreamComponent
 * closes over TProps and re-asserts it at the render boundary the parse already validated.
 */
export interface RegisteredStreamComponent {
  componentType: StreamComponentType;
  /** Also run parseProps on a part restored from chat history, which otherwise reaches render unvalidated. */
  parsesOnHydration: boolean;
  /** Widened from the typed registration's `TProps | null`; still returns null for a malformed shape. */
  parseProps: (props: Record<string, unknown>) => unknown;
  render: (props: unknown, context: StreamComponentContext) => ReactElement;
}

export interface StreamComponentOptions {
  parsesOnHydration: boolean;
}

export interface BaseComponentPart<TComponentType extends StreamComponentType, TProps> {
  type: ChatMessagePartType.COMPONENT;
  componentType: TComponentType;
  props: TProps & AnswersDraftProps;
  isNextSuggestion?: boolean;
}

export type ChatMessageComponentPart =
  | PlanTableComponentPart
  | ButtonGroupComponentPart
  | EntityDraftCardComponentPart
  | EntityDraftSkeletonComponentPart
  | EntityCardComponentPart
  | EntityCardSkeletonComponentPart
  | EntityDraftGroupComponentPart
  | EntityCardGroupComponentPart
  | OptionGridComponentPart
  | ActionListComponentPart
  | DraftDiscardedNoteComponentPart
  | WebSourcesComponentPart
  | WebSourcesSkeletonComponentPart;
